import React, {useState, useEffect} from 'react';
import {
  Card,
  Typography,
  Descriptions,
  Tag,
  List,
  Collapse,
  Space,
  Image,
} from 'antd';
import {
  PlayCircleOutlined,
  PictureOutlined,
  FileGifOutlined,
} from '@ant-design/icons';
import {
  StoryChapter,
  MediaType,
  Media,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/story/graphql/story.interface';
import {storyStorageRepo} from '../StoryStorageRepository';

const {Title, Text} = Typography;
const {Panel} = Collapse;

const isS3Url = (url: string): boolean => {
  return /^https?:\/\/(.+\.)?s3\.[a-z0-9-]+\.amazonaws\.com\//.test(url);
};

const getMediaTypeColor = (type: MediaType): string => {
  switch (type) {
    case MediaType.VIDEO:
      return 'blue';
    case MediaType.ANIMATION:
      return 'purple';
    case MediaType.PHOTO:
      return 'green';
    case MediaType.AUDIO:
      return 'orange';
    default:
      return 'default';
  }
};

const getMediaTypeIcon = (type: MediaType) => {
  switch (type) {
    case MediaType.VIDEO:
      return <PlayCircleOutlined />;
    case MediaType.ANIMATION:
      return <FileGifOutlined />;
    case MediaType.PHOTO:
      return <PictureOutlined />;
    default:
      return <PictureOutlined />;
  }
};

interface ReviewStepProps {
  metadata: any;
  chapters: Partial<StoryChapter>[];
}

const ReviewImage = ({
  src,
  alt,
  style,
  width,
}: {
  src: string;
  alt: string;
  style?: React.CSSProperties;
  width?: string | number;
}) => {
  const [url, setUrl] = useState(src);

  useEffect(() => {
    const resolve = async () => {
      if (!src) {
        setUrl('');
        return;
      }

      if (isS3Url(src)) {
        try {
          const signedUrl = await storyStorageRepo.resolveUrl(src);
          setUrl(signedUrl);
        } catch (e) {
          console.error('Error resolving review image:', e);
          setUrl(src);
        }
      } else {
        setUrl(src);
      }
    };
    resolve();
  }, [src]);

  return <Image src={url} alt={alt} style={style} width={width} />;
};

const CoverMediaPreview = ({coverMedia}: {coverMedia: Media}) => {
  const [resolvedMediaUrl, setResolvedMediaUrl] = useState<string>('');
  const [resolvedThumbnailUrl, setResolvedThumbnailUrl] = useState<string>('');

  useEffect(() => {
    const resolve = async () => {
      if (coverMedia?.mediaUrl) {
        if (isS3Url(coverMedia.mediaUrl)) {
          try {
            const signedUrl = await storyStorageRepo.resolveUrl(
              coverMedia.mediaUrl,
            );
            setResolvedMediaUrl(signedUrl);
          } catch (e) {
            setResolvedMediaUrl(coverMedia.mediaUrl);
          }
        } else {
          setResolvedMediaUrl(coverMedia.mediaUrl);
        }
      }

      if (coverMedia?.thumbnailUrl) {
        if (isS3Url(coverMedia.thumbnailUrl)) {
          try {
            const signedUrl = await storyStorageRepo.resolveUrl(
              coverMedia.thumbnailUrl,
            );
            setResolvedThumbnailUrl(signedUrl);
          } catch (e) {
            setResolvedThumbnailUrl(coverMedia.thumbnailUrl);
          }
        } else {
          setResolvedThumbnailUrl(coverMedia.thumbnailUrl);
        }
      }
    };
    resolve();
  }, [coverMedia]);

  return (
    <div style={{width: '280px'}}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '8px',
        }}>
        <Text strong>Cover Media</Text>
        <Tag
          color={getMediaTypeColor(coverMedia.type)}
          icon={getMediaTypeIcon(coverMedia.type)}>
          {coverMedia.type}
        </Tag>
      </div>
      <div style={{display: 'flex', gap: '8px'}}>
        <div
          style={{
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid #f0f0f0',
            flex: 1,
          }}>
          {coverMedia.type === MediaType.VIDEO ? (
            <video
              src={resolvedMediaUrl}
              controls
              style={{
                width: '100%',
                height: '160px',
                objectFit: 'contain',
                background: '#000',
              }}
            />
          ) : (
            <Image
              src={resolvedMediaUrl}
              alt="Cover"
              style={{width: '100%', height: '160px', objectFit: 'cover'}}
            />
          )}
        </div>
        {coverMedia.thumbnailUrl && (
          <div style={{width: '80px', flexShrink: 0}}>
            <Text
              type="secondary"
              style={{fontSize: '10px', display: 'block', marginBottom: '4px'}}>
              Thumbnail
            </Text>
            <div
              style={{
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #f0f0f0',
              }}>
              <Image
                src={resolvedThumbnailUrl}
                alt="Thumbnail"
                style={{width: '100%', height: '60px', objectFit: 'cover'}}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const ReviewStep = ({metadata, chapters}: ReviewStepProps) => {
  return (
    <Card bordered={false} style={{background: 'transparent'}}>
      <div style={{marginBottom: '24px'}}>
        <Title level={4}>Final Review</Title>
        <Text type="secondary">
          Verify the details of your story before publishing
        </Text>
      </div>

      <div style={{display: 'flex', gap: '24px', marginBottom: '24px'}}>
        <div style={{flex: 1}}>
          <Descriptions title="Story Metadata" bordered column={1} size="small">
            <Descriptions.Item label="Title (EN)">
              {metadata.title?.en}
            </Descriptions.Item>
            <Descriptions.Item label="Title (AR)">
              {metadata.title?.ar}
            </Descriptions.Item>
            <Descriptions.Item label="Category">
              <Tag color="blue">{metadata.category}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Story Order">
              <Tag color="purple">#{metadata.storyOrder}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Labels">
              <Space wrap>
                {metadata.labels?.map((l: string) => (
                  <Tag key={l}>{l}</Tag>
                ))}
              </Space>
            </Descriptions.Item>
          </Descriptions>
        </div>
        {metadata.coverMedia?.mediaUrl && (
          <CoverMediaPreview coverMedia={metadata.coverMedia} />
        )}
      </div>

      <Descriptions bordered size="small" style={{marginBottom: '24px'}}>
        <Descriptions.Item label="Description (EN)" span={2}>
          {metadata.description?.en}
        </Descriptions.Item>
        <Descriptions.Item
          label="Description (AR)"
          span={2}
          style={{direction: 'rtl'}}>
          {metadata.description?.ar}
        </Descriptions.Item>
      </Descriptions>

      <Title level={5} style={{marginBottom: '16px'}}>
        Chapter Content ({chapters.length} chapters)
      </Title>
      <Collapse ghost>
        {chapters.map((chapter, index) => (
          <Panel
            header={`Chapter ${index + 1}: ${chapter.name?.en || 'Untitled'}`}
            key={index}
            style={{
              background: '#fafafa',
              marginBottom: '8px',
              borderRadius: '8px',
              border: '1px solid #f0f0f0',
            }}>
            <Space>
              {
                <Tag color="blue">
                  Chapter duration: {chapter.chapterDuration}
                </Tag>
              }
            </Space>
            <List
              size="small"
              dataSource={chapter.paragraphs || []}
              renderItem={(para, pIdx) => (
                <List.Item>
                  <Space direction="vertical" style={{width: '100%'}}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}>
                      <Text strong>Paragraph {pIdx + 1}</Text>
                      <Space>
                        {para.paragraph_audio_url && (
                          <Tag color="blue">
                            {para.paragraph_audio_url.split('/').pop()}
                          </Tag>
                        )}
                        {para.paragraph_audio_url && (
                          <Tag color="blue">{para.paragraphDuration}</Tag>
                        )}
                        {para.media && para.media.length > 0 && (
                          <Tag color="purple">
                            {para.media.length} Media items
                          </Tag>
                        )}
                      </Space>
                    </div>
                    <div style={{display: 'flex', gap: '16px'}}>
                      <div
                        style={{
                          flex: 1,
                          padding: '8px',
                          background: '#fff',
                          borderRadius: '4px',
                          border: '1px solid #eee',
                        }}>
                        <Text style={{fontSize: '13px'}}>{para.text.en}</Text>
                      </div>
                      <div
                        style={{
                          flex: 1,
                          padding: '8px',
                          background: '#fff',
                          borderRadius: '4px',
                          border: '1px solid #eee',
                          direction: 'rtl',
                        }}>
                        <Text style={{fontSize: '13px'}}>{para.text.ar}</Text>
                      </div>
                    </div>
                    {para.media && para.media.length > 0 && (
                      <div
                        style={{
                          marginTop: '12px',
                          padding: '12px',
                          background: '#f8fafc',
                          borderRadius: '8px',
                        }}>
                        <Text
                          type="secondary"
                          style={{
                            fontSize: '12px',
                            display: 'block',
                            marginBottom: '8px',
                          }}>
                          Paragraph Media:
                        </Text>
                        <Space wrap>
                          {para.media.map((m: any, mIdx: number) => (
                            <div
                              key={mIdx}
                              style={{
                                borderRadius: '6px',
                                overflow: 'hidden',
                                border: '1px solid #e2e8f0',
                                width: '60px',
                                height: '60px',
                              }}>
                              {m.type === 'PHOTO' ? (
                                <ReviewImage
                                  src={m.mediaUrl}
                                  alt="Media"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: '#edf2f7',
                                  }}>
                                  <Text style={{fontSize: '10px'}}>
                                    {m.type}
                                  </Text>
                                </div>
                              )}
                            </div>
                          ))}
                        </Space>
                      </div>
                    )}
                  </Space>
                </List.Item>
              )}
            />
          </Panel>
        ))}
      </Collapse>
    </Card>
  );
};
